import { ArtifactRawType } from './enums';
import { Kitchen } from '../kitchens/kitchen.class';
import { ReferenceMap } from '../shared.types';

import { ArtifactClasses, ArtifactRecord, ArtifactRecords, ArtifactRecordsData, ArtifactType } from './types';
import { Component, ComponentCategory, ComponentInstanceType, ComponentVendor } from '../../component-classes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// type ClassWithGetReferenceMap<T> = T extends typeof {getReferenceMap: (string) => ReferenceMap };
interface ClassWithGetReferenceMap {
	getReferenceMap: (string) => ReferenceMap;
}

/**
 * The Artifacts hash is used to:
 *
 * 1. reduce duplicate static entries throughout the object tree
 * 	1. once a vendor, component, category or other external data element is referenced in the blueprint, that
 * data is stored and reference in the artifact hash. By doing this, we remove exernal depencies when processing
 * logic while headless and/or disconnected
 * 	2. Artifacts should be managed while items and add and removed in the blueprint object tree
 * 	3. On serialize, checks for and skips any artifact that is no longer used by any node in the blueprint design
 * 2. provide a caching mechanism (todo)
 * 	1. caching entries are not included when derializing data, this prevents the data from being persisted
 */

export class Artifacts<ParentType extends ClassWithGetReferenceMap = Kitchen> {
	#data: ArtifactRecords;
	#artifactClasses: Partial<ArtifactClasses>;
	#parent: ParentType;

	constructor (dataIn: ArtifactRecordsData | null, parent: ParentType) {
		this.#data = {};
		this.#parent = parent ?? null;
		this.#artifactClasses = {
			[ComponentInstanceType.Component]: Component,
			[ComponentInstanceType.ComponentCategory]: ComponentCategory,
			[ComponentInstanceType.ComponentVendor]: ComponentVendor
		};

		if (dataIn) {
			Object.keys(dataIn).forEach((id) => {
				const node = dataIn[id];
				if (this.#artifactClasses[node.type]) {
					this.#data[id] = {
						type: node.type,
						value: new (this.#artifactClasses[node.type])({ userEmail: node.value?.userEmail ?? null, dataIn: node.value })
					};
				} else {
					console.warn('artifact:constructor:data (invalid type)', node.type);
				}
			});
		}
	}

	public add (id: string, dataType: ArtifactRecord['type'], value: ArtifactRecord['value']): void {
		if (this.#data[value?.id]) return;

		this.#data[id] = {
			type: dataType,
			value
		};
	}

	/**
	 * delete an entry in the artifact hash using the provided id
	 * @param id item to remove if no longer referenced
	 * @returns true if no longer used and entry is removed from the hash
	 */
	public delete (id: string): boolean {
		let del = true;

		if (this.#parent) {
			const refMap = this.#parent.getReferenceMap(id);
			if (refMap !== null) del = false;
		}

		if (del) delete this.#data[id];
		return del;
		// return (this.#data[id] ?? null) === null;
	}

	// Allow setting values using dot notation
	// setProperty (id: string, value: ArtifactRecordData): void {
	// 	this.#data[id] = value;
	// }

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public findByType (artifactType: ArtifactType, cb: (a: ArtifactRecord['value']) => boolean): null | ArtifactRecord {
		const found = Object.keys(this.#data).find((id) => {
			const artifactRecord = this.#data[id];
			return artifactRecord.type === artifactType
				? cb(this.#data[id].value)
				: false;
		});
		return found ? this.#data[found] : null;
	}

	// Allow reading values using dot notation
	getProperty (id: string): ArtifactRecord | undefined {
		return this.#data[id];
	}

	public serialize (): ArtifactRecordsData {
		const retObject = {};

		Object.keys(this.#data).forEach((id) => {
			const node = this.#data[id];

			// Don't persist cache artifact records
			if (!(node.type in ArtifactRawType)) {
				const refMap = this.#parent.getReferenceMap(id);
				// only persis if blueprint records are using the artifact
				if (refMap) {
					retObject[id] = {
						type: node.type,
						data: node.value?.serialize()
					};
				}
			}
		});

		return retObject;
	}
}

